import * as React from 'react';
import {withStyles, WithStyles} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/WarningRounded';
import TranslateNew from '@translate/TranslateNew';
import Button from '@components/common/button/Button';
import ArticleLink from '@components/common/ArticleLink';
import { IChatMessage } from '@app-types/websocket/chat.types';
import { awaitsSenderAction, isRewritePending, isRewriteUsable, isToxicBlocked, isToxicFlagged,
  UNCHECKED_AFTER_MS } from '@services/chat/toxicVerdict';
import { ChatStyle } from './Chat.style';

const tn: TranslateNew = TranslateNew.getInstance();
// The server sends a flagged message that may be sent by itself two minutes after it was held.
const AUTO_SEND_MS = 120000;
const COUNTDOWN_MS = 30000;
// The sheet closes as soon as the sender acts. A message still held this much later did not get
// the action through (offline, or the server refused it), so the sheet opens again.
const ACTION_RETRY_MS = 10000;

type Edit = "ORIGINAL"|"AISUGGESTION"|"EMPTY";

interface IProps extends WithStyles<typeof ChatStyle> {
  messagesInTransit:IChatMessage[]|undefined;
  edit:(msg:IChatMessage, edit:Edit) => void;
  send:(msg:IChatMessage) => void;
}

// Asks the sender what to do with a held message, one at a time. Minimizing the sheet is the same
// as editing the message yourself.
class ChatToxicSheet extends React.Component<IProps> {
  // When this device first saw each message, as sendingTime is from the server's clock.
  private heldSince = new Map<number, number>();
  private actedOn = new Map<number, number>();
  // Kept while the sheet slides away after its message is no longer held.
  private shown:IChatMessage|undefined;
  private timer:number|undefined;
  private sheetRef = React.createRef<HTMLDivElement>();
  private dragStartY:number|undefined;
  private dragDistance = 0;

  public componentDidMount() {
    this.track();
  }

  public componentDidUpdate() {
    this.track();
  }

  public componentWillUnmount() {
    window.clearTimeout(this.timer);
  }

  public render() {
    const {classes} = this.props;
    const now = Date.now();
    const held = this.heldMessage(now);
    if (held !== undefined) {
      this.shown = held;
    }
    const msg = this.shown;

    return (<>
      <div className={classes.chatToxicSheetBackdrop + (held ? '' : ' ' + classes.chatToxicSheetBackdropClosed)}
        onClick={() => held && this.act(held, "ORIGINAL")}/>
      {msg && <div key={msg.id} ref={this.sheetRef}
        className={classes.chatToxicSheet + (held ? '' : ' ' + classes.chatToxicSheetClosed)}>
        <div className={classes.chatToxicSheetHandle}
          onTouchStart={(e) => this.dragStart(e)} onTouchMove={(e) => this.dragMove(e)}
          onTouchEnd={() => this.dragEnd(held)} onTouchCancel={() => this.dragEnd(undefined)}>
          <div className={classes.chatToxicSheetHandleBar}/>
        </div>
        <div className={classes.chatToxicSheetContent}>
          {this.renderContent(msg, now)}
        </div>
      </div>}
    </>);
  }

  private renderContent(msg:IChatMessage, now:number) {
    const {classes} = this.props;
    const blocked = isToxicBlocked(msg.toxicVerdict);
    const countdown = this.autoSendCountdown(msg, now);
    const rewriteUsable = isRewriteUsable(msg);
    // When the AI has answered without a rewrite, or no check is coming, there is nothing to offer.
    const offersRewrite = rewriteUsable || isRewritePending(msg);
    const articleLink = (text:string) =>
      <ArticleLink article={tn.lang.toxicSheetArticleLink} className={classes.chatToxicSheetArticleLink}>{text}</ArticleLink>;

    return (<>
      <div className={classes.chatToxicSheetTitle}>
        <WarningIcon className={classes.chatToxicSheetIcon}/>
        {blocked ? tn.lang.toxicSheetBlockedTitle : tn.lang.toxicSheetTitle}
      </div>
      <div className={classes.chatToxicSheetInfo}>
        {blocked ? tn.lang.toxicSheetBlockedInfo(articleLink) : tn.lang.toxicSheetInfo(articleLink)}
      </div>
      {offersRewrite
        ? <div className={classes.chatToxicSheetSuggestion}>
          <div className={classes.chatToxicSheetSuggestionLabel}>{tn.lang.toxicSheetAiSuggestion}</div>
          <div>
            {rewriteUsable ? msg.rewrite : <i className={classes.chatWorking}>{tn.lang.working}...</i>}
          </div>
        </div>
        : <div className={classes.chatToxicSheetNoSuggestion}>{tn.lang.toxicSheetNoSuggestion}</div>}
      <div className={classes.chatToxicSheetActions}>
        {offersRewrite && <Button variant='contained' color='primary' fullWidth={true}
          className={classes.chatToxicSheetAction}
          disabled={!rewriteUsable} onClick={() => this.act(msg, "AISUGGESTION")}>
          {tn.lang.toxicSheetUseSuggestion}
        </Button>}
        <Button variant={offersRewrite ? 'outlined' : 'contained'} color={offersRewrite ? 'default' : 'primary'}
          fullWidth={true} className={classes.chatToxicSheetAction}
          onClick={() => this.act(msg, "ORIGINAL")}>
          {tn.lang.toxicSheetEditMyself}
        </Button>
        {blocked
          ? <Button variant='text' color='secondary' fullWidth={true} className={classes.chatToxicSheetAction}
            onClick={() => this.act(msg, "EMPTY")}>
            {tn.lang.toxicSheetDelete}
          </Button>
          : <Button variant='text' color='secondary' fullWidth={true} className={classes.chatToxicSheetAction}
            onClick={() => this.act(msg, "SEND")}>
            {tn.lang.toxicSheetSendAnyway}{countdown !== undefined && ` (${countdown})`}
          </Button>}
      </div>
    </>);
  }

  // The first message the sender has to act on, unless they just did.
  private heldMessage(now:number):IChatMessage|undefined {
    return (this.props.messagesInTransit || []).find((msg) => {
      const actedOn = this.actedOn.get(msg.id);
      if (actedOn !== undefined && now - actedOn < ACTION_RETRY_MS) {
        return false;
      }
      const heldSince = this.heldSince.get(msg.id);
      return awaitsSenderAction(msg, heldSince === undefined ? 0 : now - heldSince);
    });
  }

  // Seconds until the server sends the message by itself, for the last half minute.
  private autoSendCountdown(msg:IChatMessage, now:number):number|undefined {
    if (!isToxicFlagged(msg.toxicVerdict) || isToxicBlocked(msg.toxicVerdict)) {
      return undefined;
    }
    const left = msg.sendingTime + AUTO_SEND_MS - now;
    return left > 0 && left <= COUNTDOWN_MS ? Math.ceil(left / 1000) : undefined;
  }

  private act(msg:IChatMessage, action:Edit|"SEND") {
    this.actedOn.set(msg.id, Date.now());
    if (action === "SEND") {
      this.props.send(msg);
    } else {
      this.props.edit(msg, action);
    }
    this.forceUpdate();
  }

  // Notes when messages arrive and renders again when a rule above changes its answer with time.
  private track() {
    const now = Date.now();
    const messages = this.props.messagesInTransit || [];
    const ids = new Set(messages.map((msg) => msg.id));
    this.heldSince.forEach((heldSince, id) => {
      if (!ids.has(id)) {
        this.heldSince.delete(id);
        this.actedOn.delete(id);
      }
    });

    let next = Infinity;
    for (const msg of messages) {
      let heldSince = this.heldSince.get(msg.id);
      if (heldSince === undefined) {
        heldSince = now;
        this.heldSince.set(msg.id, now);
      }
      if (!isToxicFlagged(msg.toxicVerdict)) {
        next = Math.min(next, this.later(heldSince + UNCHECKED_AFTER_MS, now));
      }
      const actedOn = this.actedOn.get(msg.id);
      if (actedOn !== undefined) {
        next = Math.min(next, this.later(actedOn + ACTION_RETRY_MS, now));
      }
    }
    // The countdown starts half a minute before the server sends the message and ticks each second.
    const held = this.heldMessage(now);
    if (held !== undefined && this.autoSendCountdown(held, now) !== undefined) {
      next = Math.min(next, now + ((held.sendingTime + AUTO_SEND_MS - now) % 1000 || 1000));
    } else if (held !== undefined && isToxicFlagged(held.toxicVerdict) && !isToxicBlocked(held.toxicVerdict)) {
      next = Math.min(next, this.later(held.sendingTime + AUTO_SEND_MS - COUNTDOWN_MS, now));
    }

    window.clearTimeout(this.timer);
    this.timer = next === Infinity ? undefined : window.setTimeout(() => this.forceUpdate(), next - now);
  }

  private later(time:number, now:number):number {
    return time > now ? time : Infinity;
  }

  private dragStart(e:React.TouchEvent<HTMLDivElement>) {
    this.dragStartY = e.touches[0].clientY;
    this.dragDistance = 0;
    if (this.sheetRef.current) {
      this.sheetRef.current.style.transition = 'none';
    }
  }

  private dragMove(e:React.TouchEvent<HTMLDivElement>) {
    if (this.dragStartY === undefined || !this.sheetRef.current) {
      return;
    }
    this.dragDistance = Math.max(0, e.touches[0].clientY - this.dragStartY);
    this.sheetRef.current.style.transform = `translateY(${this.dragDistance}px)`;
  }

  // Swiped down far enough, the sheet is minimized; otherwise it springs back.
  private dragEnd(held:IChatMessage|undefined) {
    const sheet = this.sheetRef.current;
    const minimize = sheet !== null && this.dragDistance > sheet.offsetHeight / 3;
    this.dragStartY = undefined;
    this.dragDistance = 0;
    if (sheet) {
      sheet.style.transition = '';
      sheet.style.transform = '';
    }
    if (minimize && held !== undefined) {
      this.act(held, "ORIGINAL");
    }
  }
}

export default withStyles(ChatStyle)(ChatToxicSheet);



// WEBPACK FOOTER //
// ./src/components/chat/ChatToxicSheet.tsx